type PopoverElement = HTMLElement & {
  showPopover?: () => void;
  hidePopover?: () => void;
  togglePopover?: () => void;
};

declare global {
  interface Window {
    CookieConsent?: {
      init: (config: Record<string, unknown>) => void;
    };
  }
}

const initCookieConsent = (): void => {
  if (typeof window.CookieConsent === 'undefined') {
    return;
  }

  window.CookieConsent.init({
    // More link URL on bar.
    modalMainTextMoreLink: null,
    // How long to wait until bar comes up.
    barTimeout: 500,
    // Look and feel.
    theme: {
      barColor: '#fff',
      barTextColor: '#272825',
      barMainButtonColor: '#007C4C',
      barMainButtonTextColor: '#fff',
      modalMainButtonColor: '#007C4C',
      modalMainButtonTextColor: '#fff',
    },
    language: {
      current: 'sv',
      locale: {
        sv: {
          barMainText: 'Denna webbplats använder cookies för att säkerställa att du får den bästa upplevelsen på vår webbplats.',
          closeAriaLabel: 'stäng',
          barLinkSetting: 'Cookie-inställningar',
          barBtnAcceptAll: 'Acceptera alla cookies',
          modalMainTitle: 'Cookie-inställningar',
          modalMainText:
            'Cookies är små datastycken som skickas från en webbplats och lagras på användarens dator av användarens webbläsare medan användaren surfar. Din webbläsare lagrar varje meddelande i en liten fil, kallad cookie. När du begär en annan sida från servern skickar din webbläsare tillbaka cookien till servern. Cookies designades för att vara en pålitlig mekanism för webbplatser att komma ihåg information eller att registrera användarens surfaktivitet.',
          modalBtnSave: 'Spara aktuella inställningar',
          modalBtnAcceptAll: 'Acceptera alla cookies och stäng',
          modalAffectedSolutions: 'Påverkade lösningar:',
          learnMore: 'Läs mer',
          on: 'På',
          off: 'Av',
          enabled: 'är aktiverad.',
          disabled: 'är inaktiverad.',
          checked: 'markerad',
          unchecked: 'omarkerad',
        },
      },
    },
    // List all the categories you want to display.
    categories: {
      necessary: {
        needed: true,
        wanted: true,
        checked: true,
        language: {
          locale: {
            sv: {
              name: 'Nödvändiga',
              description:
                'Nödvändiga cookies hjälper dig att göra en hemsida användbar, genom att aktivera grundläggande funktioner såsom sidnavigering åtkomst till säkra områden på hemsidan. Hemsidan kan inte fungera optimalt utan dessa cookies.',
            },
          },
        },
      },
      statistic: {
        needed: false,
        wanted: false,
        checked: true,
        language: {
          locale: {
            sv: {
              name: 'Statistiska ',
              description: 'Statistiska cookies hjälper hemsidans ägare att förstå hur besökare interagerar med hemsidan, genom att samla in och rapportera uppgifter.',
            },
          },
        },
      },
      marketing: {
        needed: false,
        wanted: false,
        checked: true,
        language: {
          locale: {
            sv: {
              name: 'Marknadsföring',
              description:
                'Marketingcookies används för att spåra besökare gränsöverskridande på hemsidor. Avsikten är att visa annonser som är relevanta och engagerande för den enskilda användaren och därmed vara mer värdefulla för utgivare och tredjepartsannonsörer.',
            },
          },
        },
      },
    },
    // List actual services here.
    services: {
      analytics: {
        category: 'statistic',
        type: 'dynamic-script',
        search: 'analytics',
        cookies: [
          {
            name: '_gid',
            domain: `.${window.location.hostname}`,
          },
          {
            name: /^_ga/,
            domain: `.${window.location.hostname}`,
          },
        ],
        language: {
          locale: {
            sv: {
              name: 'Google Analytics',
            },
          },
        },
      },
    },
    consentModeControls: {
      ad_storage: 'marketing',
      ad_user_data: 'marketing',
      ad_personalization: 'marketing',
      analytics_storage: 'statistic',
    },
  });
};

const MOBILE_TOC_FAB_ATTR = 'data-mobile-toc-fab-initialized';
const MOBILE_TOC_STYLE_ID = 'mobile-toc-fab-styles';
const MOBILE_TOC_POPOVER_ID = 'mobile-toc-popover';
const MOBILE_TOC_FAB_ID = 'mobile-toc-fab-button';

const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = ['a[href]', 'button', 'input', 'select', 'textarea', "[tabindex]:not([tabindex='-1'])"];
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter((el) => !el.hasAttribute('disabled'));
};

const injectStyles = (): void => {
  if (document.getElementById(MOBILE_TOC_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = MOBILE_TOC_STYLE_ID;
  style.textContent = `
    .mobile-toc-fab {
      position: fixed;
      right: 1rem;
      bottom: 5rem;
      z-index: 10001;
      width: 3rem;
      height: 3rem;
      border-radius: 999px;
      background: var(--wp--preset--color--gray-900, #111827);
      color: var(--wp--preset--color--white, #ffffff);
      border: none;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      font-size: 1rem;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: transform 180ms ease, opacity 180ms ease;
    }

    .mobile-toc-fab:focus-visible {
      outline: 2px solid var(--wp--preset--color--primary-500, #4f46e5);
      outline-offset: 3px;
    }

    @media (min-width: 782px) {
      .mobile-toc-fab {
        display: none;
      }
    }

    .mobile-toc-popover {
      z-index: 10000;
      border: none;
      border-radius: 0.75rem;
      padding: 0;
      background: transparent;
      opacity: 0;
      transform: translateY(8px);
      transition: opacity 200ms ease, transform 200ms ease;
    }

    .mobile-toc-popover:popover-open {
      opacity: 1;
      transform: translateY(0);
    }

    .mobile-toc-panel {
      width: min(90vw, 22rem);
      max-height: min(75vh, 32rem);
      background: var(--wp--preset--color--primary-200, #e0e7ff);
      color: var(--wp--preset--color--gray-900, #111827);
      padding: 1.5rem;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.2);
      border-radius: 0.75rem;
      overflow-y: auto;
    }

    .mobile-toc-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0;
    }

    .mobile-toc-close {
      appearance: none;
      background: transparent;
      border: none;
      color: inherit;
      font-size: 1.5rem;
      cursor: pointer;
      margin-left: auto;
    }

    .mobile-toc-panel nav {
      margin-top: 0.5rem;
    }

    .mobile-toc-panel a {
      display: block;
      padding: 0.35rem 0;
      color: inherit;
      text-decoration: none;
    }

    .mobile-toc-panel a:hover,
    .mobile-toc-panel a:focus-visible {
      text-decoration: underline;
    }

    .mobile-toc-title {
      font-size: 0.95rem;
      font-weight: 600;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
};

const buildPopover = (tocClone: HTMLElement): HTMLElement => {
  const popover = document.createElement('el-popover');
  popover.id = MOBILE_TOC_POPOVER_ID;
  popover.className = 'mobile-toc-popover';
  popover.setAttribute('popover', '');
  popover.setAttribute('anchor', 'top end');
  popover.setAttribute('anchor-strategy', 'fixed');
  popover.setAttribute('role', 'dialog');
  popover.setAttribute('aria-modal', 'true');
  popover.setAttribute('tabindex', '-1');
  popover.style.setProperty('--anchor-gap', '0.75rem');

  const panel = document.createElement('div');
  panel.className = 'mobile-toc-panel';

  const header = document.createElement('div');
  header.className = 'mobile-toc-header';

  const title = document.createElement('div');
  title.textContent = 'Innehållsförteckning';
  title.setAttribute('id', 'mobile-toc-title');
  title.className = 'mobile-toc-title';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'mobile-toc-close';
  closeButton.setAttribute('aria-label', 'Stäng innehållsförteckning');
  closeButton.innerHTML = '&times;';

  header.appendChild(title);
  header.appendChild(closeButton);
  panel.appendChild(header);
  panel.appendChild(tocClone);
  popover.appendChild(panel);
  popover.setAttribute('aria-labelledby', 'mobile-toc-title');

  return popover;
};

export const initMobileTocFab = (): void => {
  const run = () => {
    if (document.documentElement.hasAttribute(MOBILE_TOC_FAB_ATTR)) {
      return;
    }

    const toc = document.querySelector<HTMLElement>('nav.wp-block-table-of-contents');
    if (!toc) {
      return;
    }

    document.documentElement.setAttribute(MOBILE_TOC_FAB_ATTR, 'true');
    injectStyles();

    const tocClone = toc.cloneNode(true) as HTMLElement;
    tocClone.removeAttribute('data-cursor-element-id');
    tocClone.classList.add('mobile-toc-clone');

    const popover = buildPopover(tocClone) as PopoverElement;
    document.body.appendChild(popover);

    const fab = document.createElement('button');
    fab.id = MOBILE_TOC_FAB_ID;
    fab.type = 'button';
    fab.className = 'mobile-toc-fab';
    fab.setAttribute('aria-label', 'Öppna innehållsförteckning');
    // Assumes trusted SVG markup provided by the caller.
    fab.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
  <path fill="currentColor" d="M3 17v-1.5h13.75v1.5H3Zm0 -4.25v-1.5h13.75v1.5H3ZM3 8.5v-1.5h13.75v1.5H3Zm17.2375 8.5c-0.20835 0 -0.38335 -0.0719 -0.525 -0.21575C19.57085 16.6406 19.5 16.4625 19.5 16.25c0 -0.2 0.0719 -0.375 0.21575 -0.525 0.14365 -0.15 0.32175 -0.225 0.53425 -0.225 0.2 0 0.375 0.075 0.525 0.225 0.15 0.15 0.225 0.32915 0.225 0.5375 0 0.20835 -0.075 0.38335 -0.225 0.525 -0.15 0.14165 -0.32915 0.2125 -0.5375 0.2125Zm0 -4.25c-0.20835 0 -0.38335 -0.0719 -0.525 -0.21575C19.57085 12.3906 19.5 12.2125 19.5 12c0 -0.2 0.0719 -0.375 0.21575 -0.525 0.14365 -0.15 0.32175 -0.225 0.53425 -0.225 0.2 0 0.375 0.075 0.525 0.225 0.15 0.15 0.225 0.32915 0.225 0.5375 0 0.20835 -0.075 0.38335 -0.225 0.525 -0.15 0.14165 -0.32915 0.2125 -0.5375 0.2125Zm0 -4.25c-0.20835 0 -0.38335 -0.0719 -0.525 -0.21575C19.57085 8.1406 19.5 7.9625 19.5 7.75c0 -0.2 0.0719 -0.375 0.21575 -0.525 0.14365 -0.15 0.32175 -0.225 0.53425 -0.225 0.2 0 0.375 0.075 0.525 0.225 0.15 0.15 0.225 0.32915 0.225 0.5375 0 0.20835 -0.075 0.38335 -0.225 0.525 -0.15 0.14165 -0.32915 0.2125 -0.5375 0.2125Z" stroke-width="0.5"></path>
</svg>`;
    fab.setAttribute('popovertarget', MOBILE_TOC_POPOVER_ID);
    document.body.appendChild(fab);

    let lastFocused: HTMLElement | null = null;
    let previousOverflow = '';
    let isOpen = false;

    const openOverlay = () => {
      if (isOpen) {
        return;
      }

      lastFocused = document.activeElement as HTMLElement | null;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      isOpen = true;
      popover.showPopover?.();

      const focusables = getFocusableElements(popover);
      (focusables[0] || popover).focus();
    };

    const closeOverlay = () => {
      if (!isOpen) {
        return;
      }

      isOpen = false;
      popover.hidePopover?.();
      document.body.style.overflow = previousOverflow;

      if (lastFocused) {
        lastFocused.focus();
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeOverlay();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusables = getFocusableElements(popover);
      if (!focusables.length) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement as HTMLElement | null;

      if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    fab.addEventListener('click', (event) => {
      event.preventDefault();
      openOverlay();
    });

    popover.querySelector('.mobile-toc-close')?.addEventListener('click', closeOverlay);

    tocClone.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('a')) {
        closeOverlay();
      }
    });

    document.addEventListener('keydown', handleKeydown);
    popover.addEventListener('toggle', () => {
      const isActuallyOpen = popover.matches?.(':popover-open') ?? isOpen;
      if (!isActuallyOpen) {
        isOpen = false;
        document.body.style.overflow = previousOverflow;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
};

export default {
  init() {
    initCookieConsent();
    initMobileTocFab();
  },
  finalize() {
    // JavaScript to be fired on all pages, after page specific JS is fired
  },
};
